"use client";

import { useEffect } from "react";
import { recordRumError } from "@/lib/rum/client";

/**
 * Root error boundary. Catches render errors thrown by the root layout
 * itself, where no other boundary can.
 *
 * Its reason for existing beyond a nicer fallback: RUM's "errors" telemetry
 * listens on window.onerror and unhandledrejection, and React render errors
 * caught by a boundary never reach either. Without this, the class of bug
 * that blanks a page for a user would be invisible in the telemetry.
 *
 * global-error must render its own <html>/<body> — it replaces the root
 * layout — and is styled inline on purpose: the failure being reported may
 * itself be in the theme or styled-components tree.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    recordRumError(error);
  }, [error]);

  return (
    <html>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
          color: "#1f1f1f",
          background: "#ffffff",
        }}
      >
        <main style={{ maxWidth: "32rem", padding: "2rem", textAlign: "center" }}>
          <h1 style={{ fontSize: "1.25rem", marginBottom: "0.75rem" }}>
            Something went wrong
          </h1>
          <p style={{ margin: "0 0 1.5rem", lineHeight: 1.5 }}>
            The page could not be displayed. The problem has been reported.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              padding: "0.625rem 1.25rem",
              fontSize: "1rem",
              cursor: "pointer",
              color: "#ffffff",
              background: "#EB1700",
              border: "none",
              borderRadius: "0.25rem",
            }}
          >
            Try again
          </button>
          {error.digest && (
            // The digest is the only way to correlate this screen with the
            // server log entry for the same error.
            <p style={{ marginTop: "1.5rem", fontSize: "0.75rem", color: "#6b6b6b" }}>
              Reference: {error.digest}
            </p>
          )}
        </main>
      </body>
    </html>
  );
}
